import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import axios from 'axios'
import { useRef } from 'react'

import { storedAttribution } from '../lib/attribution'
import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'
import type { InsuranceEstimate } from './insuranceEstimate'

/**
 * The public funnel payload: the carrier facts we pulled from CAB plus the
 * estimates generated for the lead. A strict allowlist server-side — no
 * application, org or user exists in this flow, and the id is the funnel
 * session's, never the sales lead's.
 */
/** The carrier facts the funnel panels render, flattened off a FunnelSession. */
export interface FunnelCarrier {
  name: string
  dotNumber: string
  state: string
  powerUnits: number
  drivers: number
  cargoCarried: string[]
}

export function toFunnelCarrier(session: FunnelSession): FunnelCarrier {
  return {
    name: session.dbaName || session.legalName,
    dotNumber: session.dotNumber,
    state: session.state,
    powerUnits: session.powerUnits,
    drivers: session.drivers,
    cargoCarried: session.cargoCarried,
  }
}

export interface FunnelSession {
  id: string
  dotNumber: string
  legalName: string
  dbaName: string
  city: string
  state: string
  powerUnits: number
  drivers: number
  cargoCarried: string[]
  currentInsurer: string
  policyExpirationDate: string | null
  estimates: InsuranceEstimate[]
}

const BASE = '/api/v1/insure/insurance-funnel'

export function isFunnelFatalHttpError(error: unknown): boolean {
  if (!axios.isAxiosError(error) || !error.response) return false
  const code = error.response.status
  return code === 401 || code === 403 || code === 404
}

export async function startFunnelSession(dotNumber: string): Promise<FunnelSession> {
  const response = await axios.post(`${BASE}/start/`, {
    dot_number: dotNumber,
    attribution: storedAttribution(),
  })
  return toCamelCase(response.data) as FunnelSession
}

/** "Yes, that's me" — asks for nothing, and the estimates start generating. */
export async function confirmFunnelSession(id: string): Promise<FunnelSession> {
  const response = await axios.post(`${BASE}/${id}/confirm/`)
  return toCamelCase(response.data) as FunnelSession
}

/**
 * Primary CTA: capture the visitor's contact details and get them into the app.
 * An address we have never seen is signed in on the spot and proved later in the
 * chat; one that already has a Cai account still needs a code first, or typing a
 * customer's address would land you in their account.
 */
/** Where a code went: the visitor's email, or (phoneEnding set) the phone of the Cai account that holds it. */
export type CodeSent = { requiresCode: true; email: string; phoneEnding?: string | null }

export type ClaimResult = { loginUrl: string } | CodeSent

export function claimNeedsCode(result: ClaimResult): result is CodeSent {
  return 'requiresCode' in result
}

export async function claimFunnelAccount(
  id: string,
  contact: { email: string; phone: string }
): Promise<ClaimResult> {
  const response = await axios.post(`${BASE}/${id}/claim/`, {
    email: contact.email,
    phone: contact.phone,
  })
  return toCamelCase(response.data) as ClaimResult
}

/** Another copy of the code, for the address already on the session. */
export async function resendVerificationCode(id: string): Promise<CodeSent> {
  const response = await axios.post(`${BASE}/${id}/resend-code/`)
  return toCamelCase(response.data) as CodeSent
}

/** Correct code: the account now exists — this URL signs them into it. */
export async function verifyCode(id: string, code: string): Promise<string> {
  const response = await axios.post(`${BASE}/${id}/verify-code/`, { code })
  return (toCamelCase(response.data) as { loginUrl: string }).loginUrl
}

/**
 * This DOT already has a Cai account. Mail its owner a code so a returning
 * customer gets back in instead of dead-ending. The address comes back masked —
 * a guessed DOT must not reveal somebody's email.
 */
export async function sendExistingAccountCode(id: string): Promise<string> {
  const response = await axios.post(`${BASE}/${id}/claim-existing/`)
  return (toCamelCase(response.data) as { maskedEmail: string }).maskedEmail
}

/** Correct code for the account already on file: this URL signs them into it. */
export async function verifyExistingAccountCode(id: string, code: string): Promise<string> {
  const response = await axios.post(`${BASE}/${id}/verify-existing/`, { code })
  return (toCamelCase(response.data) as { loginUrl: string }).loginUrl
}

export async function recordExpertRequest(
  id: string,
  contact: { fullName: string; email?: string; phone?: string; companyWebsite?: string }
): Promise<void> {
  await axios.post(`${BASE}/${id}/expert/`, {
    full_name: contact.fullName,
    email: contact.email,
    phone: contact.phone,
    company_website: contact.companyWebsite,
  })
}

/**
 * No-DOT "new authority" funnel: capture contact + CDL-experience answer as a
 * tagged, DOT-less sales lead. No session exists in this flow.
 */
export async function recordNewAuthorityExpertRequest(contact: {
  fullName: string
  email?: string
  phone?: string
  companyWebsite?: string
  hasTwoYearCdl: boolean
}): Promise<void> {
  await axios.post(`${BASE}/new-authority-lead/`, {
    full_name: contact.fullName,
    email: contact.email,
    phone: contact.phone,
    company_website: contact.companyWebsite,
    has_two_year_cdl: contact.hasTwoYearCdl,
  })
}

export interface FunnelPollResult {
  session: FunnelSession
  signature: string
}

/**
 * Long-poll the session. The server holds the request up to ~25s, returning when
 * the estimate set changes or the deadline elapses. Pass the last-seen signature
 * so the server can detect no-change-since.
 */
export async function pollFunnelSession(
  id: string,
  signature: string,
  signal?: AbortSignal
): Promise<FunnelPollResult> {
  const response = await axios.get(`${BASE}/${id}/poll/`, {
    params: { signature },
    signal,
    // Higher than the server's 25s window so a clean timeout returns instead of
    // axios aborting first.
    timeout: 35_000,
  })
  return {
    session: toCamelCase(response.data) as FunnelSession,
    signature: response.headers['x-estimate-signature'] ?? '',
  }
}

export function useFunnelSession({
  id,
  onFatalError,
}: {
  id: string | undefined
  onFatalError: () => void
}): UseQueryResult<FunnelSession, Error> {
  const signatureRef = useRef('')

  return useQuery({
    queryKey: [CacheKeys.FUNNEL_SESSION, id],
    queryFn: async ({ signal }) => {
      const result = await pollFunnelSession(id!, signatureRef.current, signal)
      signatureRef.current = result.signature
      return result.session
    },
    enabled: !!id,
    retry: (failureCount, error) => {
      if (isFunnelFatalHttpError(error)) {
        onFatalError()
        return false
      }
      return failureCount < 2
    },
    refetchInterval: 2000,
  })
}
